import { getValue, sendRequest, ValueOrValueGetter } from '../../utils';
import { LoggerAdapter } from './types';
import { serializeMessage, serializeParams } from './utils/azureLogSerializer';

const applicationInsightsEndpoint = 'https://dc.services.visualstudio.com/v2/track';

enum AzureAppInsightsSeverityLevel {
    Verbose = 0,
    Information = 1,
    Warning = 2,
    Error = 3,
    Critical = 4,
}

export interface CreateAzureAppInsightsLoggerAdapter {
    context?: string | null;
    applicationInsightsInstrumentationKey: ValueOrValueGetter<string>;
    getNowUTC: () => string;
    sessionOptions?: Record<string, unknown>;
    onError?: (error: Error) => void;
}

const createAzureAppInsightsLoggerAdapter = ({
    context = null,
    getNowUTC,
    applicationInsightsInstrumentationKey: applicationInsightsInstrumentationKeyGetter,
    sessionOptions = {},
    onError,
}: CreateAzureAppInsightsLoggerAdapter) => {
    const instrumentationKey = getValue(applicationInsightsInstrumentationKeyGetter);

    async function doLog(severityLevel: AzureAppInsightsSeverityLevel, type: string, message: string, params = {}) {
        if (instrumentationKey) {
            let serializedParams = params;
            let sanitizedMessage = message;

            try {
                serializedParams = serializeParams({ sessionOptions, ...params }, { onError, type });
                sanitizedMessage = serializeMessage(sanitizedMessage);
            } catch (e) {
                if (onError) {
                    onError(e);
                }
            }
            const telemetryItem = {
                iKey: instrumentationKey,
                name: `Microsoft.ApplicationInsights.${instrumentationKey}.Message`,
                time: getNowUTC(),
                data: {
                    baseType: 'MessageData',
                    baseData: {
                        ver: 2,
                        message: sanitizedMessage,
                        severityLevel,
                        properties: {
                            ...serializedParams,
                            context,
                            type,
                        },
                    },
                },
            };

            return sendRequest(applicationInsightsEndpoint, { accept: '*/*' }, [telemetryItem]).catch((error) => {
                if (onError) {
                    onError(error);
                }
            });
        }
    }

    const AzureAppInsightLoggerAdapter: LoggerAdapter = {
        log: async (type: string, message: string, params = {}) =>
            doLog(AzureAppInsightsSeverityLevel.Information, type, message, params),
        warn: async (type: string, message: string, params = {}) =>
            doLog(AzureAppInsightsSeverityLevel.Warning, type, message, params),
        error: async (type: string, message: string, params = {}) =>
            doLog(AzureAppInsightsSeverityLevel.Error, type, message, params),
        info: async (type: string, message: string, params = {}) =>
            doLog(AzureAppInsightsSeverityLevel.Information, type, message, params),
    };

    return AzureAppInsightLoggerAdapter;
};

export default createAzureAppInsightsLoggerAdapter;
